/* Dice Goblin — the d20 roller on the home page (sections/hero-roll.liquid)
   A roll just for fun, for everyone: no prizes here (rolls that win store credit, one for every $20 spent, live in
   My Lair). When lair-core.js is on the page the roll comes from the Lair app (POST /roll { kind: 'fun' }, and
   ?roll=N in the demo); when it isn't, or the app is slow or away, the browser rolls. Every roll then picks a
   genuinely random product from the whole shop. Needs theme.js (window.DiceGoblin) for the cart; without it the
   pick links to the product. */
(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const config = (() => {
    try {
      return JSON.parse(document.getElementById('theme-config')?.textContent || '{}');
    } catch {
      return {};
    }
  })();
  const routes = config.routes || {};
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || routes.root || '/';
  const PAGE_SIZE = 50; // products per page of the feed: small enough to load fast on a phone
  const TRIES = 5; // random spots to try before settling for any product already loaded
  const esc = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const money = (value) => `$${(Math.round(Number(value) * 100) / 100).toFixed(2)}`;
  const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
  const shop = () => window.DiceGoblin || null;
  const lair = () => (window.Lair && window.Lair.store) || null;

  /** A fair whole number from 0 to n - 1 (crypto when there is one, so every face and product is equally likely) */
  const randomInt = (n) => {
    try {
      const limit = Math.floor(0x100000000 / n) * n;
      const box = new Uint32Array(1);
      do {
        window.crypto.getRandomValues(box);
      } while (box[0] >= limit);
      return box[0] % n;
    } catch {
      return Math.floor(Math.random() * n);
    }
  };

  /* ---------- the shop's products, for a random pick ---------- */
  const tagsOf = (p) => (Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',')).map((tag) => String(tag).trim().toLowerCase());
  const imageUrl = (src, width) => (src ? `${src}${src.includes('?') ? '&' : '?'}width=${width}` : '');

  /** A product someone can buy: not hidden, not a prize, not a library copy, membership or gift card */
  function buyable(p) {
    if (!p) return null;
    const tags = tagsOf(p);
    if (tags.includes('hidden') || tags.includes('dice-chest-prize') || tags.includes('board game rental')) return null;
    const words = `${p.title || ''} ${p.handle || ''} ${p.product_type || ''}`;
    if (/\(library\)/i.test(p.title || '') || /board-game-rental/i.test(p.handle || '') || /gift.?card|membership/i.test(words)) return null;
    const variant = (p.variants || []).find((v) => v.available && Number(v.price) > 0);
    if (!variant) return null;
    const image = (p.images && p.images[0] && p.images[0].src) || (p.image && p.image.src) || '';
    return {
      title: p.title,
      url: `${root}products/${p.handle}`,
      image: imageUrl(image, 240),
      price: money(variant.price),
      compare: Number(variant.compare_at_price) > Number(variant.price) ? money(variant.compare_at_price) : '',
      variantId: variant.id,
    };
  }

  class D20Roller extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-roll]');
      this.label = this.querySelector('[data-roll-label]');
      this.die = this.querySelector('[data-die]');
      this.number = this.querySelector('.d20__number');
      this.hint = this.querySelector('[data-hint]');
      this.result = this.querySelector('[data-result]');
      this.burst = this.querySelector('[data-burst]');
      this.count = Number(this.dataset.count) || 0;
      this.pages = new Map();
      try {
        this.pool = JSON.parse(this.querySelector('[data-pool]').textContent).filter((p) => p.v && p.a);
      } catch {
        this.pool = [];
      }
      if (!this.button || !this.die) return;
      this.button.hidden = false;
      this.button.addEventListener('click', () => this.roll());
      this.die.addEventListener('click', () => this.roll());
      // Start finding a product as soon as someone reaches for the die, so the roll feels instant.
      this.addEventListener('pointerdown', () => this.nextPick(), { passive: true });
      this.button.addEventListener('focus', () => this.nextPick());
      this.addEventListener('click', (event) => {
        const add = event.target.closest('[data-add]');
        if (add) this.addToCart(add);
      });
    }

    /* ---------- rolling ---------- */
    async roll() {
      if (this.busy) return;
      this.busy = true;
      // aria-disabled, not disabled: a keyboard roll keeps its place on the button through the tumble (a disabled
      // button drops focus to the page). busy already stops a second roll.
      this.button.setAttribute('aria-disabled', 'true');
      if (this.label) this.label.textContent = 'Rolling…';
      const pick = this.nextPick();
      const outcome = this.ask();
      await this.tumble(outcome);
      const roll = await outcome;
      this.land(roll);
      await this.show(roll, pick);
      this.picking = null;
      this.button.removeAttribute('aria-disabled');
      if (this.label) this.label.textContent = 'Roll again';
      this.busy = false;
    }

    /** The roll from the Lair app when it's on the page (never a prize), or from the browser */
    async ask() {
      const store = lair();
      if (store && typeof store.backend.roll === 'function') {
        try {
          const data = await Promise.race([store.backend.roll({ kind: 'fun' }), wait(4000).then(() => null)]);
          const roll = Number(data && data.roll);
          if (Number.isInteger(roll) && roll >= 1 && roll <= 20) return roll;
        } catch {
          /* the browser rolls instead */
        }
      }
      return 1 + randomInt(20);
    }

    async tumble(until) {
      const die = this.die;
      die.classList.remove('is-rolling', 'is-rattling', 'is-crit', 'is-lucky');
      if (reduceMotion.matches) {
        await until;
        return;
      }
      void die.offsetWidth;
      die.classList.add('is-rolling');
      const flicker = window.setInterval(() => {
        this.number.textContent = String(1 + Math.floor(Math.random() * 20));
      }, 70);
      let settled = false;
      until.then(() => {
        settled = true;
      });
      await wait(1100);
      die.classList.remove('is-rolling');
      if (!settled) {
        die.classList.add('is-rattling');
        await until;
        die.classList.remove('is-rattling');
      }
      window.clearInterval(flicker);
    }

    land(roll) {
      this.number.textContent = String(roll);
      this.die.classList.toggle('is-crit', roll === 20);
      this.die.classList.toggle('is-lucky', roll === 1);
    }

    /* ---------- the result: the number and a random product ---------- */
    async show(roll, pick) {
      const lead = roll === 20 ? 'Natural 20!' : roll === 1 ? 'Natural 1!' : `You rolled ${roll}.`;
      const said = roll === 20 ? 'Nice one, friend. Gobgob found you this.' : roll === 1 ? 'Gobgob found you something anyway.' : 'Gobgob found you this.';
      if (this.hint) this.hint.textContent = roll === 20 || roll === 1 ? `You rolled a natural ${roll}!` : `You rolled ${roll}`;
      this.result.innerHTML = `<p class="roller__says"><strong>${esc(lead)}</strong> ${esc(said)}</p><div class="roller__pick" data-pick-slot>${this.pickSkeleton()}</div>`;
      if (roll === 20) this.celebrate();
      this.reveal();
      const item = await pick;
      this.fillPick(item);
      shop()?.announce(item ? `You rolled ${roll}. Gobgob picked ${item.title}, ${item.price}.` : `You rolled ${roll}.`);
    }

    /** A burst of tiny dice inside the tray on a natural 20. Skipped when people prefer less motion. */
    celebrate() {
      if (!this.burst || reduceMotion.matches) return;
      const colours = ['var(--c-gold)', 'var(--c-text)', 'var(--c-gold)', 'var(--c-ruby)'];
      this.burst.innerHTML = '';
      const count = 16;
      for (let i = 0; i < count; i += 1) {
        const bit = document.createElement('span');
        const angle = (Math.PI * 2 * i) / count + Math.random() * 0.3;
        const dist = 70 + Math.random() * 60;
        bit.style.setProperty('--dx', `${Math.round(Math.cos(angle) * dist)}px`);
        bit.style.setProperty('--dy', `${Math.round(Math.sin(angle) * dist * 0.8)}px`);
        bit.style.setProperty('--rot', `${Math.round(Math.random() * 360 - 180)}deg`);
        bit.style.setProperty('--bit', colours[i % colours.length]);
        bit.style.animationDelay = `${Math.round(Math.random() * 120)}ms`;
        this.burst.append(bit);
      }
      window.setTimeout(() => {
        if (this.burst) this.burst.innerHTML = '';
      }, 1400);
    }

    /** On a phone the result sits under the die: once the die has landed, scroll just far enough to show it, but
        never so far that the die (and a natural 20's burst and glow) goes off the top of the screen. */
    reveal() {
      window.setTimeout(() => {
        const box = this.result.getBoundingClientRect();
        const hidden = box.bottom + 16 - window.innerHeight;
        if (hidden <= 0) return;
        const tray = this.querySelector('[data-tray]');
        const trayTop = tray ? tray.getBoundingClientRect().top : box.top;
        const by = Math.min(hidden, box.top - 96, trayTop - 16);
        if (by > 0) window.scrollBy({ top: by, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }, reduceMotion.matches ? 0 : 500);
    }

    /* ---------- the random product ---------- */
    nextPick() {
      if (!this.picking) this.picking = this.randomProduct().catch(() => this.fallbackPick());
      return this.picking;
    }

    /** One page of the shop's product feed. A page past the end is empty; a failed load can be tried again. */
    async page(n) {
      if (!this.pages.has(n)) {
        const load = fetch(`${root}collections/all/products.json?limit=${PAGE_SIZE}&page=${n}`, { headers: { Accept: 'application/json' } })
          .then((r) => {
            if (!r.ok) throw new Error(`Products page ${n}: ${r.status}`);
            return r.json();
          })
          .then((data) => (Array.isArray(data.products) ? data.products : []));
        load.catch(() => this.pages.delete(n));
        this.pages.set(n, load);
      }
      return this.pages.get(n);
    }

    /** Every product in the shop is equally likely: pick a random spot in the whole catalogue and take what's
        there. When that's something nobody can buy (a library copy, a hidden prize, sold out) or the last pick,
        pick a new spot. After a few misses, any buyable product from the pages already loaded. */
    async randomProduct() {
      const lastUrl = this.lastPick && this.lastPick.url;
      let total = this.count || PAGE_SIZE * 6;
      for (let attempt = 0; attempt < TRIES; attempt += 1) {
        const spot = randomInt(total);
        const n = Math.floor(spot / PAGE_SIZE) + 1;
        let products;
        try {
          products = await this.page(n);
        } catch {
          continue;
        }
        const index = spot % PAGE_SIZE;
        if (index >= products.length) {
          // The feed has fewer products than the count (it skips unpublished ones): count only what's there
          total = Math.max(1, Math.min(total, (n - 1) * PAGE_SIZE + products.length));
          continue;
        }
        const item = buyable(products[index]);
        if (item && item.url !== lastUrl) return item;
      }
      const loaded = await Promise.allSettled([...this.pages.values()]);
      const candidates = loaded
        .flatMap((r) => (r.status === 'fulfilled' ? r.value : []))
        .map(buyable)
        .filter((p) => p && p.url !== lastUrl);
      return candidates.length ? candidates[randomInt(candidates.length)] : this.fallbackPick();
    }

    fallbackPick() {
      const list = this.pool.filter((p) => !this.lastPick || p.u !== this.lastPick.url);
      const p = list.length ? list[randomInt(list.length)] : null;
      return p ? { title: p.t, url: p.u, image: p.i || '', price: p.p, compare: '', variantId: p.v } : null;
    }

    pickSkeleton() {
      return '<div class="roll-pick roll-pick--loading" aria-hidden="true"><span class="roll-pick__media"></span><span class="roll-pick__text"><span class="roll-pick__bar"></span><span class="roll-pick__bar roll-pick__bar--short"></span></span></div>';
    }

    fillPick(item) {
      const slot = this.result.querySelector('[data-pick-slot]');
      if (!slot) return;
      if (!item) {
        slot.remove();
        return;
      }
      this.lastPick = item;
      slot.innerHTML = `<article class="roll-pick">
        <a class="roll-pick__media" href="${esc(item.url)}" tabindex="-1" aria-hidden="true">${item.image ? `<img src="${esc(item.image)}" alt="" width="120" height="120" loading="lazy">` : ''}</a>
        <div class="roll-pick__text">
          <p class="roll-pick__label">Gobgob picked this for you</p>
          <h3 class="roll-pick__title"><a href="${esc(item.url)}">${esc(item.title)}</a></h3>
          <p class="price${item.compare ? ' price--sale' : ''}"><span class="price__current">${esc(item.price)}</span>${item.compare ? `<s class="price__compare">${esc(item.compare)}</s>` : ''}</p>
        </div>
        <button class="button button--small roll-pick__add" type="button" data-add="${esc(item.variantId)}">Add to cart</button>
      </article>`;
    }

    /* ---------- cart ---------- */
    async addToCart(button) {
      const api = shop();
      if (button.getAttribute('aria-busy') === 'true') return;
      if (!api) {
        window.location.href = this.lastPick ? this.lastPick.url : `${root}collections/all`;
        return;
      }
      button.setAttribute('aria-busy', 'true');
      try {
        const data = await api.cartRequest(`${routes.cartAdd || `${root}cart/add`}.js`, { items: [{ id: Number(button.dataset.add), quantity: 1 }] });
        api.applySections(data.sections);
        api.diceBurst(button);
        api.announce('Added to your cart');
        button.textContent = 'In your cart';
        api.openCart(button);
      } catch (error) {
        api.announce(error.message);
        button.textContent = 'Sold out, sorry';
        button.disabled = true;
      } finally {
        button.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('d20-roller', D20Roller);
})();
